import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from './Layout';
import { campaignsAPI, campaignProvisionsAPI, quarterlyProvisionsAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { 
  Calendar,
  Banknote,
  Sparkles,
  GripHorizontal,
  ChevronLeft,
  ChevronRight,
  Users,
  TrendingUp,
  Loader2,
  Info,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Upload,
  FileSpreadsheet,
  X,
  Calculator,
  FileText,
  Plus,
  Trash2
} from 'lucide-react';
import ProvisionManagerExtended from './ProvisionManagerExtended';
import TrainerProvisionView from './TrainerProvisionView';
import GehaltsrechnerTrainee from './GehaltsrechnerTrainee';
import DienstplanGenerator from './DienstplanGenerator';
import TagesplanungView from './TagesplanungView';

// Helper to get week number from date
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Get date from week number
const getDateFromWeek = (year, week) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return ISOweekStart;
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value || 0);
};

// Parse CSV/Excel content
const parseCSV = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];
  
  // Detect delimiter (comma, semicolon, tab)
  const firstLine = lines[0];
  let delimiter = ',';
  if (firstLine.includes(';')) delimiter = ';';
  else if (firstLine.includes('\t')) delimiter = '\t';
  
  // Parse header
  const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
  
  // Find column indices
  const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('trainee') || h === 'n');
  const unitsIdx = headers.findIndex(h => h.includes('einheit') || h.includes('unit') || h.includes('e') || h === 'einheiten');
  const emailIdx = headers.findIndex(h => h.includes('email') || h.includes('mail') || h.includes('e-mail'));
  const phoneIdx = headers.findIndex(h => h.includes('telefon') || h.includes('phone') || h.includes('tel') || h.includes('handy'));
  
  // Parse data rows
  const trainees = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(delimiter).map(v => v.trim().replace(/['"]/g, ''));
    
    const name = nameIdx >= 0 ? values[nameIdx] : values[0];
    const units = unitsIdx >= 0 ? values[unitsIdx] : values[1];
    const email = emailIdx >= 0 ? values[emailIdx] : '';
    const phone = phoneIdx >= 0 ? values[phoneIdx] : '';
    
    if (name && name.length > 0) {
      trainees.push({
        name: name,
        units: parseFloat(units?.replace(',', '.')) || 0,
        email: email || '',
        phone: phone || ''
      });
    }
  }
  
  return trainees;
};

const TimelineDashboard = ({ embedded = false }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [selectedYear, setSelectedYear] = useState('2025');
  const [campaigns, setCampaigns] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearlyTotal, setYearlyTotal] = useState(0);
  const [yearlyNetto, setYearlyNetto] = useState(0);
  const [campaignTotals, setCampaignTotals] = useState({});
  const [campaignNettos, setCampaignNettos] = useState({});
  const [campaignPayouts, setCampaignPayouts] = useState({});
  const [quarterlyTotals, setQuarterlyTotals] = useState({});
  const [quarterlyCounts, setQuarterlyCounts] = useState({});
  const [newCampaignOpen, setNewCampaignOpen] = useState(false);
  const [newCampaignStart, setNewCampaignStart] = useState('');
  const [newCampaignEnd, setNewCampaignEnd] = useState('');
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  
  // Admin: All trainers' data for overview mode
  const [allTrainersData, setAllTrainersData] = useState({}); // {trainerId: {campaigns: {campaignId: total}}}
  
  // Touch/Swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Drag state
  const [draggedCampaign, setDraggedCampaign] = useState(null);
  
  // Move campaign modal state
  const [moveCampaignOpen, setMoveCampaignOpen] = useState(false);
  const [campaignToMove, setCampaignToMove] = useState(null);
  const [newStartWeek, setNewStartWeek] = useState('');
  const [movingCampaign, setMovingCampaign] = useState(false);
  
  // Modal state
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);
  const [selectedCampaignForModal, setSelectedCampaignForModal] = useState(null);
  const [selectedQuarterForModal, setSelectedQuarterForModal] = useState(null);
  
  // CSV Import state
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvTargetCampaign, setCsvTargetCampaign] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Gehaltsrechner state (for trainers)
  const [gehaltsrechnerOpen, setGehaltsrechnerOpen] = useState(false);
  const [dienstplanOpen, setDienstplanOpen] = useState(false);
  const [tagesplanungOpen, setTagesplanungOpen] = useState(false);
  
  const scrollRef = useRef(null);
  
  // Trainer color palette - distinct colors for each trainer
  const trainerColors = [
    { bg: 'bg-gradient-to-r from-blue-500 to-blue-600', text: 'text-white', hex: '#3b82f6' },
    { bg: 'bg-gradient-to-r from-emerald-500 to-emerald-600', text: 'text-white', hex: '#10b981' },
    { bg: 'bg-gradient-to-r from-orange-500 to-orange-600', text: 'text-white', hex: '#f97316' },
    { bg: 'bg-gradient-to-r from-purple-500 to-purple-600', text: 'text-white', hex: '#a855f7' },
    { bg: 'bg-gradient-to-r from-rose-500 to-rose-600', text: 'text-white', hex: '#f43f5e' },
    { bg: 'bg-gradient-to-r from-cyan-500 to-cyan-600', text: 'text-white', hex: '#06b6d4' },
    { bg: 'bg-gradient-to-r from-amber-500 to-amber-600', text: 'text-white', hex: '#f59e0b' },
    { bg: 'bg-gradient-to-r from-indigo-500 to-indigo-600', text: 'text-white', hex: '#6366f1' },
    { bg: 'bg-gradient-to-r from-pink-500 to-pink-600', text: 'text-white', hex: '#ec4899' },
    { bg: 'bg-gradient-to-r from-teal-500 to-teal-600', text: 'text-white', hex: '#14b8a6' },
  ];
  
  // Get trainer color by index
  const getTrainerColor = (trainerId) => {
    const trainerIndex = trainers.findIndex(t => t.id === trainerId);
    return trainerColors[trainerIndex % trainerColors.length];
  };
  
  // Generate all weeks of the year
  const weeks = Array.from({ length: 52 }, (_, i) => i + 1);
  
  // Quarter markers
  const quarters = [
    { q: 1, label: 'Q1', startWeek: 1, endWeek: 13, color: 'bg-purple-500' },
    { q: 2, label: 'Q2', startWeek: 14, endWeek: 26, color: 'bg-indigo-500' },
    { q: 3, label: 'Q3', startWeek: 27, endWeek: 39, color: 'bg-violet-500' },
    { q: 4, label: 'Q4', startWeek: 40, endWeek: 52, color: 'bg-fuchsia-500' },
  ];

  // Detect campaign overlaps
  const getOverlappingCampaigns = useCallback(() => {
    const overlaps = [];
    for (let i = 0; i < campaigns.length; i++) {
      for (let j = i + 1; j < campaigns.length; j++) {
        const camp1 = campaigns[i];
        const camp2 = campaigns[j];
        const pos1 = getCampaignPosition(camp1);
        const pos2 = getCampaignPosition(camp2);
        
        // Check if campaigns overlap
        if (pos1.startWeek <= pos2.endWeek && pos2.startWeek <= pos1.endWeek) {
          overlaps.push({ camp1: camp1.id, camp2: camp2.id });
        }
      }
    }
    return overlaps;
  }, [campaigns]);

  // Load data
  useEffect(() => {
    loadData();
  }, [selectedYear]);  // Admin always sees all trainers, no need to watch selectedTrainer

  const loadData = async () => {
    setLoading(true);
    try {
      // Load campaigns
      const campaignsData = await campaignsAPI.getAll(parseInt(selectedYear));
      setCampaigns(campaignsData);
      
      // Load trainers if admin - always load all trainers data
      if (isAdmin) {
        const { usersAPI } = await import('../api');
        const trainersData = await usersAPI.getAll();
        const trainersList = trainersData.filter(t => t.role === 'trainer');
        setTrainers(trainersList);
        
        // Always load all trainers' data for overview (admin only sees overview)
        if (trainersList.length > 0) {
          await loadAllTrainersDataOptimized(campaignsData, trainersList);
        }
      }
      
      // Calculate yearly totals
      await calculateYearlyTotals(campaignsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };
  
  // Optimized: Load all trainers' data in ONE API call
  const loadAllTrainersDataOptimized = async (campaignsData, trainersList) => {
    const allData = {};
    
    // Initialize data structure for all trainers
    trainersList.forEach(trainer => {
      allData[trainer.id] = { campaigns: {}, quarterly: {} };
    });
    
    try {
      // Load ALL campaign provisions in one call
      const allProvisions = await campaignProvisionsAPI.getAll(parseInt(selectedYear));
      
      let sumBrutto = 0;
      let sumNetto = 0;
      
      // Group by trainer
      allProvisions.forEach(provision => {
        const trainerId = provision.user_id;
        if (allData[trainerId] && (provision.total_provision_brutto > 0 || (provision.trainees?.length || 0) > 0)) {
          allData[trainerId].campaigns[provision.campaign_id] = {
            total: provision.total_provision_brutto,
            netto: provision.total_provision_netto || 0,
            trainees: (() => {
              const names = new Set((provision.trainees || []).map(t => (t.name || '').trim().toLowerCase()).filter(Boolean));
              return names.size || (provision.trainees?.length || 0);
            })()
          };
          sumBrutto += provision.total_provision_brutto || 0;
          sumNetto += provision.total_provision_netto || 0;
        }
      });
      
      // Load quarterly provisions for all trainers (batch)
      const qSums = {};
      const qCounts = {};
      for (const trainer of trainersList) {
        for (let q = 1; q <= 4; q++) {
          try {
            const qProvision = await quarterlyProvisionsAPI.get(trainer.id, parseInt(selectedYear), q);
            if (qProvision && ((qProvision.trainees?.length || 0) > 0 || qProvision.total_provision_brutto > 0)) {
              allData[trainer.id].quarterly[q] = {
                total: qProvision.total_provision_brutto,
                trainees: qProvision.trainees?.length || 0
              };
              sumBrutto += qProvision.total_provision_brutto || 0;
              sumNetto += qProvision.total_provision_netto || 0;
              qSums[q] = (qSums[q] || 0) + (qProvision.total_provision_brutto || 0);
              qCounts[q] = (qCounts[q] || 0) + (qProvision.trainees?.length || 0);
            }
          } catch (e) {
            // No quarterly provision
          }
        }
      }
      
      // Admin header shows the sum of ALL trainers
      if (isAdmin) {
        setYearlyTotal(sumBrutto);
        setYearlyNetto(sumNetto);
        setQuarterlyTotals(qSums);
        setQuarterlyCounts(qCounts);
      }
    } catch (error) {
      console.error('Error loading all provisions:', error);
    }
    
    setAllTrainersData(allData);
  };

  const calculateYearlyTotals = async (campaignsData) => {
    // Admin totals are computed in loadAllTrainersDataOptimized (sum of all trainers)
    if (isAdmin) return;
    const trainerId = user?.id;
    if (!trainerId) {
      setYearlyTotal(0);
      setYearlyNetto(0);
      setCampaignTotals({});
      setCampaignNettos({});
      setCampaignPayouts({});
      setQuarterlyTotals({});
      return;
    }

    let total = 0;
    let totalNetto = 0;
    const campTotals = {};
    const campNettos = {};
    const campPayouts = {};
    const qTotals = {};

    try {
      // Get campaign provisions
      for (const campaign of campaignsData) {
        try {
          let provision;
          if (isAdmin) {
            provision = await campaignProvisionsAPI.get(trainerId, campaign.id);
          } else {
            provision = await campaignProvisionsAPI.getMine(campaign.id);
          }
          if (provision) {
            const campTotal = provision.total_provision_brutto || 0;
            campTotals[campaign.id] = campTotal;
            campNettos[campaign.id] = provision.total_provision_netto || 0;
            total += campTotal;
            totalNetto += provision.total_provision_netto || 0;
            
            // Payout status from backend (includes checked-off fee_received trainees)
            const totalPaid = (provision.total_payouts || 0) + (provision.total_advances || 0);
            const remaining = typeof provision.remaining_amount === 'number'
              ? provision.remaining_amount
              : campTotal - totalPaid;
            campPayouts[campaign.id] = {
              total: campTotal,
              paid: totalPaid,
              remaining: remaining,
              isPaid: remaining <= 0 && campTotal > 0
            };
          }
        } catch (e) {
          // No provision for this campaign
        }
      }

      // Get quarterly provisions
      for (let q = 1; q <= 4; q++) {
        try {
          let qProvision;
          if (isAdmin) {
            qProvision = await quarterlyProvisionsAPI.get(trainerId, parseInt(selectedYear), q);
          } else {
            qProvision = await quarterlyProvisionsAPI.getMine(parseInt(selectedYear), q);
          }
          if (qProvision) {
            const qTotal = qProvision.total_provision_brutto || 0;
            qTotals[q] = qTotal;
            total += qTotal;
          }
        } catch (e) {
          // No quarterly provision
        }
      }
    } catch (error) {
      console.error('Error calculating totals:', error);
    }

    setYearlyTotal(total);
    setYearlyNetto(totalNetto);
    setCampaignTotals(campTotals);
    setCampaignNettos(campNettos);
    setCampaignPayouts(campPayouts);
    setQuarterlyTotals(qTotals);
  };

  // Get campaign position on timeline
  const getCampaignPosition = (campaign) => {
    const startWeek = campaign.calendar_weeks?.[0] || getWeekNumber(campaign.start_date);
    const endWeek = campaign.calendar_weeks?.[1] || campaign.calendar_weeks?.[0] || startWeek;
    const width = (endWeek - startWeek + 1);
    return { startWeek, endWeek, width };
  };

  // Handle campaign click
  const handleCampaignClick = (campaign) => {
    setSelectedCampaignForModal(campaign);
    setSelectedQuarterForModal(null);
    setProvisionModalOpen(true);
  };

  // Handle quarter click
  const handleQuarterClick = (quarter) => {
    setSelectedQuarterForModal(quarter);
    setSelectedCampaignForModal(null);
    setProvisionModalOpen(true);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swipe left - scroll right
        scrollTimeline('right');
      } else {
        // Swipe right - scroll left
        scrollTimeline('left');
      }
    }
  };

  // Drag handlers for Admin
  const handleDragStart = (e, campaign, trainerId = null) => {
    if (!isAdmin) return;
    e.stopPropagation();
    // Store both campaign and trainer info for proper decoupling
    setDraggedCampaign({ ...campaign, trainerId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ 
      campaignId: campaign.id, 
      trainerId: trainerId 
    }));
    // Set a drag image
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 50, 35);
    }
  };

  const handleDragOver = (e, week) => {
    if (!isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetWeek) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAdmin || !draggedCampaign) {
      setDraggedCampaign(null);
      return;
    }
    
    const campaign = draggedCampaign;
    const trainerId = draggedCampaign.trainerId; // May be null for single trainer view
    const { startWeek, endWeek } = getCampaignPosition(campaign);
    const duration = endWeek - startWeek;
    const newStartWeek = targetWeek;
    const newEndWeek = newStartWeek + duration;
    
    // Reset drag state immediately
    setDraggedCampaign(null);
    
    if (newEndWeek > 52 || newStartWeek < 1) {
      return;
    }
    
    // Don't update if position hasn't changed
    if (newStartWeek === startWeek) {
      return;
    }
    
    try {
      // If trainerId is set (from "Alle Trainer" overview), use trainer-specific move
      if (trainerId) {
        // Move only this trainer's provision to a new/existing campaign
        await campaignsAPI.moveTrainerProvision(
          trainerId,
          campaign.id,
          newStartWeek,
          newEndWeek,
          parseInt(selectedYear)
        );
      } else {
        // Classic behavior: move the entire campaign (affects all trainers)
        // Calculate new dates
        const newStartDate = getDateFromWeek(parseInt(selectedYear), newStartWeek);
        const newEndDate = getDateFromWeek(parseInt(selectedYear), newEndWeek + 1);
        newEndDate.setDate(newEndDate.getDate() - 1);
        
        // Generate new name with updated KW numbers
        const newName = `KW ${newStartWeek}-${newEndWeek} / ${selectedYear}`;
        
        await campaignsAPI.update(campaign.id, {
          name: newName,
          start_date: newStartDate.toISOString().split('T')[0],
          end_date: newEndDate.toISOString().split('T')[0],
          calendar_weeks: [newStartWeek, newEndWeek],
          status: campaign.status
        });
      }
      
      // Reload all data
      await loadData();
    } catch (error) {
      console.error('Error updating campaign:', error);
      alert('Fehler beim Verschieben: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleDragEnd = () => {
    setDraggedCampaign(null);
  };

  // Manual move campaign function (fallback for drag & drop)
  const openMoveCampaignDialog = (campaign, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const { startWeek } = getCampaignPosition(campaign);
    setCampaignToMove(campaign);
    setNewStartWeek(String(startWeek));
    setMoveCampaignOpen(true);
  };

  const handleMoveCampaign = async () => {
    if (!campaignToMove || !newStartWeek) return;
    
    setMovingCampaign(true);
    
    const { startWeek, endWeek } = getCampaignPosition(campaignToMove);
    const duration = endWeek - startWeek;
    const targetStartWeek = parseInt(newStartWeek);
    const targetEndWeek = targetStartWeek + duration;
    
    if (targetEndWeek > 52 || targetStartWeek < 1) {
      alert('Ungültige Kalenderwoche');
      setMovingCampaign(false);
      return;
    }
    
    try {
      // If this block has a trainerId (from "Alle Trainer" view), use trainer-specific move
      if (campaignToMove.trainerId) {
        await campaignsAPI.moveTrainerProvision(
          campaignToMove.trainerId,
          campaignToMove.id,
          targetStartWeek,
          targetEndWeek,
          parseInt(selectedYear)
        );
      } else {
        // Classic behavior: move the entire campaign
        const newStartDate = getDateFromWeek(parseInt(selectedYear), targetStartWeek);
        const newEndDate = getDateFromWeek(parseInt(selectedYear), targetEndWeek + 1);
        newEndDate.setDate(newEndDate.getDate() - 1);
        
        const newName = `KW ${targetStartWeek}-${targetEndWeek} / ${selectedYear}`;
        
        await campaignsAPI.update(campaignToMove.id, {
          name: newName,
          start_date: newStartDate.toISOString().split('T')[0],
          end_date: newEndDate.toISOString().split('T')[0],
          calendar_weeks: [targetStartWeek, targetEndWeek],
          status: campaignToMove.status
        });
      }
      
      // Reload all data
      await loadData();
      
      setMoveCampaignOpen(false);
      setCampaignToMove(null);
    } catch (error) {
      console.error('Error moving campaign:', error);
      alert('Fehler beim Verschieben: ' + (error.response?.data?.detail || error.message));
    }
    
    setMovingCampaign(false);
  };

  // Scroll timeline
  const scrollTimeline = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // CSV Import handlers
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        const parsed = parseCSV(content);
        setCsvData(parsed);
      }
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    if (!csvTargetCampaign || csvData.length === 0 || !selectedTrainer) return;
    
    setCsvImporting(true);
    try {
      for (const trainee of csvData) {
        // Map CSV data to the expected API format
        await campaignProvisionsAPI.addTrainee(selectedTrainer, csvTargetCampaign, {
          name: trainee.name,
          units_week1: trainee.units, // Put all units in week1 for CSV import
          units_week2: 0,
          units_week3: 0,
          email: trainee.email || '',
          phone: trainee.phone || '',
          address: '',
          fee_received: 0
        });
      }
      
      // Reload data
      await loadData();
      setCsvModalOpen(false);
      setCsvData([]);
      setCsvTargetCampaign(null);
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Fehler beim Importieren: ' + (error.response?.data?.detail || error.message));
    }
    setCsvImporting(false);
  };

  // ISO week -> Monday date
  const getDateOfISOWeek = (week, year) => {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const monday = new Date(simple);
    if (dow <= 4) monday.setDate(simple.getDate() - simple.getDay() + 1);
    else monday.setDate(simple.getDate() + 8 - simple.getDay());
    return monday;
  };

  const handleCreateCampaign = async () => {
    const start = parseInt(newCampaignStart);
    const end = parseInt(newCampaignEnd || newCampaignStart);
    if (!start || end < start) return;
    setCreatingCampaign(true);
    try {
      const year = parseInt(selectedYear);
      const weeks = [];
      for (let w = start; w <= end; w++) weeks.push(w);
      const startDate = getDateOfISOWeek(start, year);
      const endDate = getDateOfISOWeek(end, year);
      endDate.setDate(endDate.getDate() + 6);
      await campaignsAPI.create({
        name: `KW ${start}-${end} / ${year}`,
        year,
        calendar_weeks: weeks,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        status: 'planned'
      });
      setNewCampaignOpen(false);
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Erstellen der Kampagne');
    }
    setCreatingCampaign(false);
  };

  const handleDeletePlannedCampaign = async (campaign) => {
    if (!window.confirm(`Geplante Kampagne "${campaign.name}" wirklich löschen?`)) return;
    try {
      await campaignsAPI.delete(campaign.id);
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Current week indicator
  const currentWeek = getWeekNumber(new Date());
  const isCurrentYear = parseInt(selectedYear) === new Date().getFullYear();
  
  // Get overlapping campaigns
  const overlaps = getOverlappingCampaigns();
  const overlappingIds = new Set(overlaps.flatMap(o => [o.camp1, o.camp2]));

  const Wrapper = embedded ? 'div' : Layout;

  if (loading) {
    return (
      <Wrapper>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#4b95d1]" />
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="space-y-4 sm:space-y-6">
        {/* Header with Year Total */}
        <div className="bg-gradient-to-r from-[#1c2e52] to-[#3e4350] rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Left side - Title and Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#4b95d1]" />
                  Jahresübersicht
                </h1>
                <p className="text-sm text-[#e6d7bf] mt-1">
                  {isAdmin ? 'Kampagnen verwalten & verschieben' : 'Deine Provisionen im Überblick'}
                </p>
              </div>
              
              {/* Year Selection */}
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[100px] bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Admin sees "Alle Trainer" badge instead of dropdown */}
              {isAdmin && (
                <>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg border border-white/20">
                    <Users className="w-4 h-4 text-white" />
                    <span className="text-white font-medium">Alle Trainer</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTagesplanungOpen(true)}
                    className="bg-emerald-600/80 border-emerald-500 text-white hover:bg-emerald-600"
                    data-testid="tagesplanung-button"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Tagesplanung</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setNewCampaignStart('');
                      setNewCampaignEnd('');
                      setNewCampaignOpen(true);
                    }}
                    className="bg-[#4b95d1]/80 border-[#4b95d1] text-white hover:bg-[#4b95d1]"
                    data-testid="new-campaign-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Neue Kampagne</span>
                  </Button>
                </>
              )}
              
              {/* CSV Import Button removed - Admin works in overview mode */}
              
              {/* Gehaltsrechner Button (Trainer only) */}
              {!isAdmin && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGehaltsrechnerOpen(true)}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    data-testid="gehaltsrechner-button"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Gehaltsrechner</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDienstplanOpen(true)}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    data-testid="dienstplan-button"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Wochenplan</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTagesplanungOpen(true)}
                    className="bg-emerald-600/80 border-emerald-500 text-white hover:bg-emerald-600"
                    data-testid="tagesplanung-button"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Tagesplanung</span>
                  </Button>
                </>
              )}
            </div>
            
            {/* Right side - Yearly Total */}
            <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
              <div className="bg-[#4b95d1] p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-[#e6d7bf]">Jahresprovision {selectedYear}</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {formatCurrency(yearlyTotal)}
                </p>
                {yearlyTotal > 0 && (
                  <p className="text-xs text-[#e6d7bf]">Netto: {formatCurrency(yearlyNetto)}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Overlap warning removed per user request */}
        </div>

        {/* Timeline */}
        <Card className="shadow-lg overflow-hidden">
          <CardHeader className="bg-[#3e4350] text-white py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#4b95d1]" />
                Kalenderwochen {selectedYear}
              </CardTitle>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => scrollTimeline('left')}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => scrollTimeline('right')}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {/* Quarter Labels */}
            <div className="flex border-b bg-gray-50">
              {quarters.map(q => (
                <div 
                  key={q.q}
                  className="flex-1 text-center py-2 border-r last:border-r-0 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleQuarterClick(q.q)}
                  data-testid={`quarter-${q.q}-label`}
                >
                  <Badge className={`${q.color} text-white`}>
                    {q.label} - Neuwerber
                  </Badge>
                  {(quarterlyTotals[q.q] > 0 || quarterlyCounts[q.q] > 0) && (
                    <p className="text-xs text-gray-600 mt-1">
                      {quarterlyTotals[q.q] > 0 ? formatCurrency(quarterlyTotals[q.q]) : ''}
                      {quarterlyCounts[q.q] > 0 ? `${quarterlyTotals[q.q] > 0 ? ' · ' : ''}${quarterlyCounts[q.q]} Neuwerber` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
            
            {/* Timeline scroll container with touch support */}
            <div 
              ref={scrollRef}
              className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 touch-pan-x"
              style={{ scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="relative min-w-[2000px] h-[200px]">
                {/* Week markers - header row */}
                <div className="absolute top-0 left-0 right-0 h-8 flex border-b bg-gray-50">
                  {weeks.map(week => (
                    <div 
                      key={week}
                      className={`flex-shrink-0 w-[38px] text-center text-xs border-r flex items-center justify-center ${
                        isCurrentYear && week === currentWeek 
                          ? 'bg-[#4b95d1] text-white font-bold' 
                          : 'text-gray-500'
                      }`}
                    >
                      {week}
                    </div>
                  ))}
                </div>
                
                {/* Drop zones for drag & drop (full height columns) - only visible when dragging */}
                {isAdmin && draggedCampaign && (
                  <div className="absolute top-8 left-0 right-0 bottom-0 flex z-20">
                    {weeks.map(week => (
                      <div 
                        key={`drop-${week}`}
                        className="flex-shrink-0 w-[38px] h-full border-r border-dashed border-[#4b95d1]/30 bg-[#4b95d1]/5 hover:bg-[#4b95d1]/30 hover:border-[#4b95d1] transition-colors"
                        onDragOver={(e) => handleDragOver(e, week)}
                        onDrop={(e) => handleDrop(e, week)}
                      />
                    ))}
                  </div>
                )}
                
                {/* Quarter dividers */}
                {quarters.map(q => (
                  <div
                    key={`divider-${q.q}`}
                    className="absolute top-8 bottom-0 w-px bg-purple-300"
                    style={{ left: `${(q.endWeek) * 38}px` }}
                  />
                ))}
                
                {/* Campaign blocks */}
                <div className="absolute top-12 left-0 right-0 bottom-4 px-1">
                  {/* Admin: Always show overview with SEPARATE blocks per trainer */}
                  {isAdmin && trainers.length > 0 ? (
                    // Render separate blocks for each trainer-campaign combination
                    (() => {
                      const allBlocks = [];
                      
                      // Group blocks by start week for vertical positioning
                      const blocksByWeek = {};
                      
                      // First pass: collect all trainer-campaign pairs
                      trainers.forEach((trainer) => {
                        const trainerData = allTrainersData[trainer.id];
                        if (!trainerData) return;
                        
                        const color = getTrainerColor(trainer.id);
                        
                        campaigns.forEach((campaign) => {
                          const campData = trainerData.campaigns[campaign.id];
                          if (!campData) return;
                          
                          const { startWeek, endWeek, width } = getCampaignPosition(campaign);
                          
                          // Track blocks at this start week for vertical stacking
                          if (!blocksByWeek[startWeek]) {
                            blocksByWeek[startWeek] = [];
                          }
                          
                          blocksByWeek[startWeek].push({
                            trainer,
                            campaign,
                            campData,
                            color,
                            startWeek,
                            endWeek,
                            width
                          });
                        });
                      });
                      
                      // Planned (manually created) campaigns without any trainer data
                      campaigns.forEach((campaign) => {
                        if (!campaign.manual) return;
                        const hasData = trainers.some(t => allTrainersData[t.id]?.campaigns?.[campaign.id]);
                        if (hasData) return;
                        const { startWeek, endWeek, width } = getCampaignPosition(campaign);
                        if (!blocksByWeek[startWeek]) blocksByWeek[startWeek] = [];
                        blocksByWeek[startWeek].push({ planned: true, campaign, startWeek, endWeek, width });
                      });
                      
                      // Second pass: render blocks with proper vertical positioning
                      Object.values(blocksByWeek).forEach((blocksAtWeek) => {
                        blocksAtWeek.forEach((blockData, idx) => {
                          if (blockData.planned) {
                            const { campaign, startWeek, endWeek, width } = blockData;
                            allBlocks.push(
                              <div
                                key={`planned-${campaign.id}`}
                                className="absolute h-[65px] rounded-lg border-2 border-dashed border-[#4b95d1]/70 bg-[#4b95d1]/10 text-[#1c2e52] group"
                                style={{
                                  left: `${(startWeek - 1) * 38}px`,
                                  width: `${width * 38 - 4}px`,
                                  top: `${idx * 72}px`,
                                  zIndex: 5
                                }}
                                data-testid={`planned-campaign-${campaign.id}`}
                              >
                                <div className="p-2 h-full flex flex-col justify-between">
                                  <div className="flex items-center justify-between">
                                    <span className="font-semibold text-xs truncate text-[#4b95d1]">Geplant</span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePlannedCampaign(campaign);
                                      }}
                                      className="opacity-0 group-hover:opacity-100 bg-red-100 hover:bg-red-200 text-red-500 rounded p-0.5 transition-opacity"
                                      title="Kampagne löschen"
                                      data-testid={`delete-planned-${campaign.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <div className="text-[10px] opacity-80">KW {startWeek}-{endWeek}</div>
                                </div>
                              </div>
                            );
                            return;
                          }
                          const { trainer, campaign, campData, color, startWeek, endWeek, width } = blockData;
                          const left = (startWeek - 1) * 38;
                          const blockWidth = width * 38 - 4;
                          
                          allBlocks.push(
                            <div
                              key={`${trainer.id}-${campaign.id}`}
                              draggable={true}
                              onDragStart={(e) => handleDragStart(e, campaign, trainer.id)}
                              onDragEnd={handleDragEnd}
                              className={`absolute h-[65px] rounded-lg shadow-md transition-all hover:scale-[1.02] hover:shadow-lg cursor-grab active:cursor-grabbing group ${
                                draggedCampaign?.id === campaign.id && draggedCampaign?.trainerId === trainer.id 
                                  ? 'opacity-50 scale-95' 
                                  : ''
                              } ${color.bg} ${color.text}`}
                              style={{
                                left: `${left}px`,
                                width: `${blockWidth}px`,
                                top: `${idx * 72}px`,
                                zIndex: draggedCampaign?.id === campaign.id && draggedCampaign?.trainerId === trainer.id ? 30 : 10
                              }}
                              data-testid={`trainer-campaign-${trainer.id}-${campaign.id}`}
                            >
                              <div className="p-2 h-full flex flex-col justify-between">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-xs truncate flex-1">
                                    {trainer.name.split(' ')[0]}
                                  </span>
                                  {/* Move button - shows on hover */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCampaignToMove({ ...campaign, trainerId: trainer.id });
                                      setNewStartWeek('');
                                      setMoveCampaignOpen(true);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 bg-white/30 hover:bg-white/50 rounded p-0.5 transition-opacity"
                                    title="Verschieben"
                                  >
                                    <GripHorizontal className="w-3 h-3" />
                                  </button>
                                </div>
                                <div 
                                  className="flex-1 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTrainer(trainer.id);
                                    setSelectedCampaignForModal(campaign);
                                    setSelectedQuarterForModal(null);
                                    setProvisionModalOpen(true);
                                  }}
                                >
                                  <div className="text-[10px] opacity-80 truncate">
                                    KW {startWeek}-{endWeek}{campData.netto > 0 ? ` · N: ${formatCurrency(campData.netto)}` : ''}
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-bold truncate">
                                      {formatCurrency(campData.total)}
                                    </span>
                                    <span className="text-[10px] opacity-75 flex-shrink-0">
                                      {campData.trainees} Tr.
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      });
                      
                      return allBlocks;
                    })()
                  ) : (
                    // Single trainer view (existing logic)
                    campaigns
                      // Filter: Trainer sieht nur Kampagnen mit Inhalt (Provision > 0)
                      .filter(campaign => isAdmin || (campaignTotals[campaign.id] && campaignTotals[campaign.id] > 0))
                      .map((campaign, index) => {
                      const { startWeek, endWeek, width } = getCampaignPosition(campaign);
                      const left = (startWeek - 1) * 38;
                      const blockWidth = width * 38 - 4;
                      const total = campaignTotals[campaign.id] || 0;
                      const payout = campaignPayouts[campaign.id];
                      const hasOverlap = overlappingIds.has(campaign.id);
                      
                      return (
                        <div
                          key={campaign.id}
                          draggable={isAdmin}
                          onDragStart={(e) => handleDragStart(e, campaign)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => {
                            // Only open modal if not dragging
                            if (!draggedCampaign) {
                              handleCampaignClick(campaign);
                            }
                          }}
                          className={`absolute h-[70px] rounded-lg shadow-md transition-all hover:scale-[1.02] hover:shadow-lg ${
                            isAdmin ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                          } ${
                            draggedCampaign?.id === campaign.id ? 'opacity-50 scale-95' : ''
                          } ${
                            hasOverlap ? 'ring-2 ring-amber-400 ring-offset-1' : ''
                          } ${
                            campaign.status === 'active' 
                              ? 'bg-gradient-to-r from-[#4b95d1] to-[#3e4350] text-white' 
                              : campaign.status === 'completed'
                              ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                              : 'bg-gradient-to-r from-[#e6d7bf] to-[#d4c5a9] text-[#1c2e52]'
                          }`}
                          style={{
                            left: `${left}px`,
                            width: `${blockWidth}px`,
                            top: `${(index % 2) * 80}px`,
                            zIndex: draggedCampaign?.id === campaign.id ? 30 : 10
                          }}
                          data-testid={`campaign-block-${campaign.id}`}
                        >
                          <div className="p-2 h-full flex flex-col justify-between relative">
                            {/* Overlap warning icon */}
                            {hasOverlap && (
                              <div className="absolute -top-2 -right-2 bg-amber-400 rounded-full p-0.5">
                                <AlertTriangle className="w-3 h-3 text-amber-900" />
                              </div>
                            )}
                            
                            {/* Payment status indicator */}
                            {payout && (
                              <div className={`absolute -top-2 -left-2 rounded-full p-0.5 ${
                                payout.isPaid ? 'bg-green-500' : 'bg-orange-400'
                              }`}>
                                {payout.isPaid ? (
                                  <CheckCircle2 className="w-3 h-3 text-white" />
                                ) : (
                                  <Clock className="w-3 h-3 text-white" />
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-sm truncate">
                                KW {startWeek}-{endWeek}
                              </span>
                              {isAdmin && (
                                <button
                                  onClick={(e) => openMoveCampaignDialog(campaign, e)}
                                  className="p-1 rounded hover:bg-white/20 transition-colors"
                                  title="Kampagne verschieben"
                                >
                                  <GripHorizontal className="w-4 h-4 opacity-70 hover:opacity-100" />
                                </button>
                              )}
                            </div>
                            <div className="text-xs opacity-90 truncate">
                              {campaign.name}
                            </div>
                            {total > 0 && (
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold truncate">
                                  {formatCurrency(total)}
                                  {campaignNettos[campaign.id] > 0 && (
                                    <span className="text-[9px] opacity-75 ml-1">N: {formatCurrency(campaignNettos[campaign.id])}</span>
                                  )}
                                </span>
                                {payout && !payout.isPaid && payout.remaining > 0 && (
                                  <span className="text-[10px] opacity-75">
                                    offen: {formatCurrency(payout.remaining)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                
                {/* Current week indicator line */}
                {isCurrentYear && (
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                    style={{ left: `${(currentWeek - 0.5) * 38}px` }}
                  >
                    <div className="absolute -top-1 -left-2 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-[8px] text-white font-bold">H</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-4 p-3 bg-gray-50 border-t text-xs">
              {/* Trainer color legend for admin (always overview mode now) */}
              {isAdmin && trainers.length > 0 ? (
                <>
                  <span className="font-semibold text-gray-600 mr-2">Trainer:</span>
                  {trainers.map((trainer) => {
                    const color = getTrainerColor(trainer.id);
                    const hasData = allTrainersData[trainer.id] && 
                      (Object.keys(allTrainersData[trainer.id].campaigns).length > 0 ||
                       Object.keys(allTrainersData[trainer.id].quarterly).length > 0);
                    if (!hasData) return null;
                    return (
                      <div 
                        key={trainer.id} 
                        className="flex items-center gap-1"
                      >
                        <div 
                          className="w-4 h-4 rounded" 
                          style={{ backgroundColor: color.hex }}
                        />
                        <span>{trainer.name.split(' ')[0]}</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 ml-auto text-[#4b95d1]">
                    <Info className="w-4 h-4" />
                    <span className="hidden sm:inline">Drag & Drop: Blöcke individuell pro Trainer verschieben</span>
                    <span className="sm:hidden">Blöcke verschieben</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gradient-to-r from-[#4b95d1] to-[#3e4350]" />
                    <span>Aktiv</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gradient-to-r from-[#e6d7bf] to-[#d4c5a9]" />
                    <span>Geplant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gradient-to-r from-gray-400 to-gray-500" />
                    <span>Abgeschlossen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckCircle2 className="w-3 h-3 text-white" />
                    </div>
                    <span>Bezahlt</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-orange-400 flex items-center justify-center">
                      <Clock className="w-3 h-3 text-white" />
                    </div>
                    <span>Offen</span>
                  </div>
                  {isCurrentYear && (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-1 bg-red-500" />
                      <span>Heute</span>
                    </div>
                  )}
                  {isAdmin && (
                    <div className="flex items-center gap-2 ml-auto text-[#4b95d1]">
                      <Info className="w-4 h-4" />
                      <span className="hidden sm:inline">Kampagnen per Drag & Drop verschieben</span>
                      <span className="sm:hidden">Touch & Wischen</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {quarters.map(q => (
            <Card 
              key={q.q}
              className={`cursor-pointer hover:shadow-lg transition-all border-l-4 ${
                q.q === 1 ? 'border-l-purple-500' :
                q.q === 2 ? 'border-l-indigo-500' :
                q.q === 3 ? 'border-l-violet-500' :
                'border-l-fuchsia-500'
              }`}
              onClick={() => handleQuarterClick(q.q)}
              data-testid={`quarter-${q.q}-card`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{q.label} Neuwerber</p>
                    <p className="text-lg sm:text-xl font-bold text-[#1c2e52]">
                      {formatCurrency(quarterlyTotals[q.q] || 0)}
                    </p>
                  </div>
                  <Badge className={`${q.color} text-white`}>
                    W3
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CSV Import Modal */}
        <Dialog open={csvModalOpen} onOpenChange={setCsvModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#4b95d1]" />
                CSV/Excel Import
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Campaign Selection */}
              <div className="space-y-2">
                <Label>Ziel-Kampagne</Label>
                <Select value={csvTargetCampaign || ''} onValueChange={setCsvTargetCampaign}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kampagne wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map(camp => (
                      <SelectItem key={camp.id} value={camp.id}>
                        {camp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* File Upload */}
              <div className="space-y-2">
                <Label>CSV/Excel Datei</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="mb-2"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Datei auswählen
                  </Button>
                  <p className="text-xs text-gray-500">
                    CSV mit Spalten: Name, Einheiten, E-Mail (optional), Telefon (optional)
                  </p>
                </div>
              </div>
              
              {/* Preview */}
              {csvData.length > 0 && (
                <div className="space-y-2">
                  <Label>Vorschau ({csvData.length} Trainees)</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Einheiten</th>
                          <th className="p-2 text-left">E-Mail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.slice(0, 10).map((t, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{t.name}</td>
                            <td className="p-2">{t.units}</td>
                            <td className="p-2 text-gray-500">{t.email || '-'}</td>
                          </tr>
                        ))}
                        {csvData.length > 10 && (
                          <tr className="border-t bg-gray-50">
                            <td colSpan={3} className="p-2 text-center text-gray-500">
                              ... und {csvData.length - 10} weitere
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCsvModalOpen(false);
                setCsvData([]);
                setCsvTargetCampaign(null);
              }}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleCsvImport}
                disabled={!csvTargetCampaign || csvData.length === 0 || csvImporting}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]"
              >
                {csvImporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {csvData.length} Trainees importieren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Campaign Dialog */}
        <Dialog open={newCampaignOpen} onOpenChange={setNewCampaignOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#4b95d1]" />
                Neue Kampagne planen
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Die Kampagne erscheint als "Geplant"-Block im Zeitstrahl ({selectedYear}) und ist sofort in der Provisions-Verwaltung auswählbar.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Start-KW</Label>
                  <Input
                    type="number"
                    min="1"
                    max="52"
                    placeholder="z.B. 31"
                    value={newCampaignStart}
                    onChange={(e) => {
                      const v = e.target.value;
                      setNewCampaignStart(v);
                      const num = parseInt(v);
                      if (num >= 1 && num <= 52) {
                        setNewCampaignEnd(String(Math.min(num + 1, 52)));
                      }
                    }}
                    data-testid="new-campaign-start-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>End-KW</Label>
                  <Input
                    type="number"
                    min={newCampaignStart || '1'}
                    max="52"
                    placeholder="z.B. 32"
                    value={newCampaignEnd}
                    onChange={(e) => setNewCampaignEnd(e.target.value)}
                    data-testid="new-campaign-end-input"
                  />
                </div>
              </div>
              {newCampaignStart && (
                <p className="text-xs text-[#4b95d1] font-medium">
                  Kampagne: KW {newCampaignStart}-{newCampaignEnd || newCampaignStart} / {selectedYear}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewCampaignOpen(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleCreateCampaign}
                disabled={!newCampaignStart || creatingCampaign}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                data-testid="new-campaign-create-btn"
              >
                {creatingCampaign ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Kampagne erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Provision Modal */}
        {isAdmin ? (
          <ProvisionManagerExtended 
            open={provisionModalOpen}
            onClose={() => {
              setProvisionModalOpen(false);
              setSelectedTrainer(null); // Reset to overview mode
              loadData(); // Refresh data after closing
            }}
            initialTrainer={selectedTrainer}
            initialCampaign={selectedCampaignForModal?.id}
            initialQuarter={selectedQuarterForModal}
            initialYear={parseInt(selectedYear)}
          />
        ) : (
          <Dialog open={provisionModalOpen} onOpenChange={setProvisionModalOpen}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-[#4b95d1]" />
                  {selectedCampaignForModal 
                    ? `Provision: ${selectedCampaignForModal.name}`
                    : selectedQuarterForModal
                    ? `Q${selectedQuarterForModal} Neuwerber ${selectedYear}`
                    : 'Provision Details'
                  }
                </DialogTitle>
              </DialogHeader>
              <TrainerProvisionView 
                filterCampaignId={selectedCampaignForModal?.id}
                filterQuarter={selectedQuarterForModal}
                filterYear={parseInt(selectedYear)}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Move Campaign Dialog */}
        <Dialog open={moveCampaignOpen} onOpenChange={setMoveCampaignOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GripHorizontal className="w-5 h-5 text-[#4b95d1]" />
                Kampagne verschieben
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {campaignToMove && (
                <div className="bg-gray-100 rounded-lg p-3">
                  {campaignToMove.trainerId && (
                    <p className="text-xs text-[#4b95d1] font-medium mb-1">
                      Nur für: {trainers.find(t => t.id === campaignToMove.trainerId)?.name || 'Trainer'}
                    </p>
                  )}
                  <p className="font-medium">{campaignToMove.name}</p>
                  <p className="text-sm text-gray-500">
                    Aktuell: KW {getCampaignPosition(campaignToMove).startWeek}-{getCampaignPosition(campaignToMove).endWeek}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Neue Start-Kalenderwoche</Label>
                <Select value={newStartWeek} onValueChange={setNewStartWeek}>
                  <SelectTrigger>
                    <SelectValue placeholder="KW wählen" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {Array.from({ length: 52 }, (_, i) => i + 1).map(week => (
                      <SelectItem key={week} value={String(week)}>
                        KW {week}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newStartWeek && campaignToMove && (
                  <p className="text-sm text-[#4b95d1]">
                    → KW {newStartWeek}-{parseInt(newStartWeek) + (getCampaignPosition(campaignToMove).endWeek - getCampaignPosition(campaignToMove).startWeek)} / {selectedYear}
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveCampaignOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleMoveCampaign}
                disabled={movingCampaign || !newStartWeek}
                className="bg-[#4b95d1] hover:bg-[#1c2e52]"
              >
                {movingCampaign ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <GripHorizontal className="w-4 h-4 mr-2" />
                )}
                Verschieben
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Gehaltsrechner Modal (Trainer only) */}
        {!isAdmin && (
          <>
            <GehaltsrechnerTrainee 
              open={gehaltsrechnerOpen} 
              onClose={() => setGehaltsrechnerOpen(false)} 
            />
            <DienstplanGenerator
              open={dienstplanOpen}
              onClose={() => setDienstplanOpen(false)}
            />
          </>
        )}
        
        {/* Tagesplanung Modal (Both Admin and Trainer) */}
        <TagesplanungView
          open={tagesplanungOpen}
          onClose={() => setTagesplanungOpen(false)}
        />
      </div>
    </Wrapper>
  );
};

export default TimelineDashboard;
